import { frameId } from "../assets/manifest.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropInstance } from "../document/types.ts";
import type { ValidationIssue } from "../editor/adapter.ts";
import { frameSizeOf } from "./behaviour.ts";
import type { CompileContext } from "./context.ts";
import { footprintFromCells } from "./footprint-override.ts";
import type { PropFootprint } from "./footprint.ts";

/**
 * The footprint a placed prop blocks by (ADR-040 amendment of 2026-10-04): its own cells when it carries
 * them, else the footprint its frame shares with every other copy. Every reader of a plain prop's footprint
 * asks here (the stamp, the tiles rule's skip, the cell-source lookup, the laid audit and the align measure),
 * so a prop's own footprint is read by all of them or by none. A frame's deck is read here too
 * (`passageFootprint`, ADR-017's amendment of 2026-10-05): the passage stamp, the overlay's openings and the
 * decks' surfaces ask one function, so a prop opens exactly the cells the overlay shows and the surface heard
 * there is the one its footprint names.
 */

/**
 * The footprint of a plain prop: its own cells painted onto its frame's size as the library's override cells
 * are (`footprintFromCells`), so they follow every rule a frame's footprint follows once stamped, else the
 * frame's footprint from the compile context. A cell outside the frame's cell grid is dropped. A prop whose
 * frame the context cannot size keeps the frame's answer, and so does a prop with a behaviour, which blocks by
 * its states (`stateFootprint`) and carries no footprint of its own.
 *
 * A prop's own footprint holds blocking cells alone, so the frame's passage and its surface are carried onto
 * it: a bridge given its own cells keeps its deck and what is heard on it. Where its own cells lie on the
 * frame's passage they block, as a frame's blocking runs do over its own passage, since whatever blocks is
 * laid after the ground is opened.
 *
 * @param prop The placed prop.
 * @param context The compile context, for the frame's footprint and size.
 * @param tileSize Tile size in pixels.
 * @returns The footprint, an empty one for own cells that block nothing, or undefined when the prop has no
 *   footprint at all, which leaves it to a tiles rule.
 */
export function blockingFootprint(prop: PropInstance, context: Pick<CompileContext, "collision" | "frameSizes">, tileSize: number): PropFootprint | undefined {
  const shared = context.collision.footprints?.[frameId(prop.frame.tileset, null, prop.frame.tile)];
  if (prop.footprint === undefined || prop.behaviour !== undefined) {
    return shared;
  }
  const size = frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile));
  if (size === undefined) {
    return shared;
  }
  const own = footprintFromCells(prop.footprint.cells, size.w, size.h, tileSize / COLLISION_CELLS_PER_TILE);
  return shared?.passage === undefined || shared.passage.length === 0 ? own : { ...own, passage: shared.passage, ...(shared.surface === undefined ? {} : { surface: shared.surface }) };
}

/**
 * Whether a context's rules lay a placed prop's passage: the game declares an open mask and the build that
 * wrote the rules marked them `passages`.
 *
 * @param rules The collision rules.
 * @returns True when a passage opens cells.
 */
export function laysPassages(rules: CompileContext["collision"]): boolean {
  return (rules.open ?? 0) !== 0 && rules.passages === true;
}

/**
 * The footprint whose passage a placed prop lays (ADR-017's amendment of 2026-10-05): the one it blocks by
 * (`blockingFootprint`), when that holds a passage and the passage is laid at all. None is laid where the game
 * declares no open mask or the context's build laid no passage (`laysPassages`), where the game stamps no
 * footprints, and under a behaviour prop, whose collision follows its state at run time.
 *
 * @param prop The placed prop.
 * @param context The compile context, for the rules, the frame's footprint and its size.
 * @param tileSize Tile size in pixels.
 * @returns The footprint, its `passage` holding at least one run, or undefined when the prop opens nothing.
 */
export function passageFootprint(prop: PropInstance, context: Pick<CompileContext, "collision" | "frameSizes">, tileSize: number): PropFootprint | undefined {
  const rules = context.collision;
  if (prop.behaviour !== undefined || !laysPassages(rules) || !rules.rules.some((rule) => rule.kind === "footprints")) {
    return undefined;
  }
  const footprint = blockingFootprint(prop, context, tileSize);
  return footprint?.passage === undefined || footprint.passage.length === 0 ? undefined : footprint;
}

/**
 * The props of a map that carry a footprint of their own, in document order.
 *
 * @param map The map.
 * @returns Each such prop's layer and id.
 */
export function propsWithOwnFootprint(map: MapDocument): { readonly layerId: string; readonly propId: string }[] {
  return map.layers.flatMap((layer) => (layer.kind === "prop" ? layer.props.filter((prop) => prop.footprint !== undefined).map((prop) => ({ layerId: layer.id, propId: prop.id })) : []));
}

/**
 * The kinds of the issues that concern a prop's collision, which the inspector's Collision tab flags: a behaviour
 * that must block but blocks nothing, a behaviour state whose frame has no footprint, and own cells outside the
 * frame.
 */
const FOOTPRINT_ISSUE_KINDS: ReadonlySet<string> = new Set(["behaviour.must-block", "behaviour.no-footprint", "prop.own-footprint"]);

/**
 * Whether an issue concerns a prop's collision, so a panel can flag the place where it is fixed.
 *
 * @param issue The issue.
 * @returns True for an issue of one of the footprint kinds.
 */
export function concernsFootprint(issue: ValidationIssue): boolean {
  return issue.kind !== undefined && FOOTPRINT_ISSUE_KINDS.has(issue.kind);
}

/**
 * A warning for each prop whose own footprint holds cells outside its frame's cell grid: such cells block
 * nothing, and they are what a frame changed by hand, or art imported again at another size, leaves behind.
 * A prop whose frame the context cannot size is not judged.
 *
 * @param map The map.
 * @param context The compile context, for the frames' sizes.
 * @param tileSize Tile size in pixels.
 * @returns The warnings, in document order.
 */
export function ownFootprintIssues(map: MapDocument, context: Pick<CompileContext, "collision" | "frameSizes">, tileSize: number): ValidationIssue[] {
  const cell = tileSize / COLLISION_CELLS_PER_TILE;
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const size = prop.footprint === undefined ? undefined : frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile));
      if (prop.footprint === undefined || size === undefined) {
        continue;
      }
      const columns = Math.ceil(size.w / cell);
      const rows = Math.ceil(size.h / cell);
      const outside = prop.footprint.cells.filter(([column, up]) => column >= columns || up >= rows).length;
      if (outside > 0) {
        issues.push({
          mapId: map.id,
          kind: "prop.own-footprint",
          layerId: layer.id,
          propId: prop.id,
          severity: "warning",
          message: `prop ${prop.id}'s own footprint has ${outside} ${outside === 1 ? "cell" : "cells"} outside its frame, which ${outside === 1 ? "blocks" : "block"} nothing; redraw it in Collision and drawing or reset it to the shared footprint`,
        });
      }
    }
  }
  return issues;
}
